import { Elena, html } from '@elenajs/core'
import {
  filter,
  find,
  findIndex,
  isNonNullish,
  map,
  pipe,
  uniqueBy
} from 'remeda'
import { isColor, solidBackgroundForegroundColor } from '../../helpers/colors'
import type { Color } from '../../types/colors'
import styles from './stepper.css' with { type: 'css' }

export type StepperStep = Readonly<{
  color: Color
  title: string
  value: string
}>

export type StepperChangeDetail = Readonly<{
  value: string
}>

type StepperTemplate = ReturnType<typeof html>

const isRecord = (value: unknown): value is Readonly<Record<string, unknown>> =>
  value !== null && typeof value === 'object'

const isString = (value: unknown): value is string => typeof value === 'string'

const normalizeStepperStep = (value: unknown): StepperStep | undefined =>
  isRecord(value) && isString(value.value) && isString(value.title)
    ? {
        color: isColor(value.color) ? value.color : 'gray',
        title: value.title,
        value: value.value
      }
    : undefined

const parseSteps = (value: string | null): readonly StepperStep[] => {
  try {
    const parsed: unknown = JSON.parse(value ?? '[]')

    return Array.isArray(parsed)
      ? pipe(
          parsed,
          map(normalizeStepperStep),
          filter(isNonNullish),
          uniqueBy((step) => step.value)
        )
      : []
  } catch {
    return []
  }
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

const tokenColor = (color: Color, shade: 4 | 10 | 12): string =>
  `var(--color-${color}-${shade})`

const tooltipColor = (color: Color): string =>
  solidBackgroundForegroundColor(color)

const stepIndex = (steps: readonly StepperStep[], value: string): number =>
  pipe(
    steps,
    findIndex((step) => step.value === value)
  ) + 1

const stepFromEvent = (event: Event): number | undefined => {
  const element = pipe(
    event.composedPath(),
    find(
      (target): target is HTMLElement =>
        target instanceof HTMLElement && target.hasAttribute('data-step')
    )
  )
  const value = Number(element?.dataset.step)

  return Number.isInteger(value) ? value : undefined
}

const isTooltipEvent = (event: Event): boolean =>
  pipe(
    event.composedPath(),
    find(
      (target) =>
        target instanceof HTMLElement &&
        target.getAttribute('role') === 'tooltip'
    )
  ) !== undefined

export class FacadeStepperElement extends Elena(HTMLElement) {
  static tagName = 'facade-stepper'
  static shadow = 'open' as const
  static styles = styles
  static props = [
    { name: 'steps', reflect: false },
    { name: 'label', reflect: false },
    'value',
    'readonly',
    'disabled',
    'no-tooltips'
  ]

  steps: readonly StepperStep[] = []
  value = ''
  label = ''
  readonly = false
  disabled = false
  'no-tooltips' = false

  private hoverValue = 0

  get noTooltips(): boolean {
    return this['no-tooltips']
  }

  set noTooltips(value: boolean) {
    this['no-tooltips'] = value
  }

  override attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    if (name === 'steps') {
      if (oldValue !== newValue) {
        this.steps = parseSteps(newValue)
      }
      return
    }

    super.attributeChangedCallback(name, oldValue, newValue)
  }

  override connectedCallback(): void {
    super.connectedCallback()
    this.addEventListener('click', this.handleClick)
    this.addEventListener('keydown', this.handleKeyDown)
    this.addEventListener('pointerenter', this.handlePointerHover)
    this.addEventListener('pointermove', this.handlePointerHover)
    this.addEventListener('pointerleave', this.handlePointerLeave)
    this.addEventListener('pointerdown', this.handlePointerDown)
    this.addEventListener('pointerup', this.handlePointerUp)
    this.addEventListener('pointercancel', this.handlePointerCancel)
  }

  override disconnectedCallback(): void {
    this.removeEventListener('click', this.handleClick)
    this.removeEventListener('keydown', this.handleKeyDown)
    this.removeEventListener('pointerenter', this.handlePointerHover)
    this.removeEventListener('pointermove', this.handlePointerHover)
    this.removeEventListener('pointerleave', this.handlePointerLeave)
    this.removeEventListener('pointerdown', this.handlePointerDown)
    this.removeEventListener('pointerup', this.handlePointerUp)
    this.removeEventListener('pointercancel', this.handlePointerCancel)
    this.clearHover()
    super.disconnectedCallback()
  }

  override updated(): void {
    super.updated()
    this.syncAccessibility()
  }

  private readonly isRtl = (): boolean => {
    const explicitDirection = this.closest<HTMLElement>('[dir]')?.dir

    return explicitDirection
      ? explicitDirection === 'rtl'
      : this.ownerDocument.defaultView?.getComputedStyle(this).direction ===
          'rtl'
  }

  private readonly valueFromCoordinate = (coordinate: number): number => {
    const { left, right, width } = this.getBoundingClientRect()

    if (width <= 0 || this.steps.length === 0) {
      return 0
    }

    const progress = this.isRtl()
      ? (right - coordinate) / width
      : (coordinate - left) / width

    return clamp(Math.ceil(progress * this.steps.length), 0, this.steps.length)
  }

  private readonly setHoverValue = (value: number): void => {
    if (value === this.hoverValue) {
      return
    }

    this.hoverValue = value
    this.requestUpdate()
  }

  private readonly setHoverFromPointer = (event: PointerEvent): void => {
    this.setHoverValue(
      stepFromEvent(event) ?? this.valueFromCoordinate(event.clientX)
    )
  }

  private readonly clearHover = (): void => {
    if (this.hoverValue === 0) {
      return
    }

    this.hoverValue = 0

    if (this.isConnected) {
      this.requestUpdate()
    }
  }

  private readonly handleClick = (event: MouseEvent): void => {
    if (this.disabled || this.readonly || isTooltipEvent(event)) {
      return
    }

    this.selectStep(
      stepFromEvent(event) ?? this.valueFromCoordinate(event.clientX)
    )
  }

  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    if (this.disabled || this.readonly || this.steps.length === 0) {
      return
    }

    const selected = stepIndex(this.steps, this.value)
    const current = Math.max(selected, 0)
    const decrement =
      event.key === 'ArrowDown' ||
      (event.key === 'ArrowLeft' && !this.isRtl()) ||
      (event.key === 'ArrowRight' && this.isRtl())
    const increment =
      event.key === 'ArrowUp' ||
      (event.key === 'ArrowRight' && !this.isRtl()) ||
      (event.key === 'ArrowLeft' && this.isRtl())
    const next = decrement
      ? Math.max(1, current - 1)
      : increment
        ? Math.min(this.steps.length, current + 1)
        : event.key === 'Home'
          ? 1
          : event.key === 'End'
            ? this.steps.length
            : undefined

    if (next === undefined) {
      return
    }

    event.preventDefault()
    this.selectStep(next)
  }

  private readonly handlePointerHover = (event: PointerEvent): void => {
    if (isTooltipEvent(event)) {
      return
    }

    this.setHoverFromPointer(event)
  }

  private readonly handlePointerLeave = (): void => {
    this.clearHover()
  }

  private readonly handlePointerDown = (event: PointerEvent): void => {
    if (event.button !== 0 || isTooltipEvent(event)) {
      return
    }

    if (!(this.disabled || this.readonly)) {
      this.focus({ preventScroll: true })
    }

    this.setHoverFromPointer(event)
    this.setPointerCapture?.(event.pointerId)
    event.preventDefault()
  }

  private readonly handlePointerUp = (event: PointerEvent): void => {
    if (this.hasPointerCapture?.(event.pointerId)) {
      this.releasePointerCapture(event.pointerId)
    }

    this.clearHover()
  }

  private readonly handlePointerCancel = (event: PointerEvent): void => {
    this.handlePointerUp(event)
  }

  private readonly selectStep = (value: number): void => {
    const step = this.steps[value - 1]

    if (!step || step.value === this.value) {
      return
    }

    this.clearHover()
    this.value = step.value

    this.updateComplete.then(() => {
      this.dispatchEvent(
        new CustomEvent<StepperChangeDetail>('facade-change', {
          bubbles: true,
          composed: true,
          detail: { value: step.value }
        })
      )
    })
  }

  private readonly syncAccessibility = (): void => {
    const selected = stepIndex(this.steps, this.value)
    const selectedStep = this.steps[selected - 1]

    this.role = 'slider'
    this.tabIndex = this.disabled || this.readonly ? -1 : 0
    this.setAttribute('aria-valuemin', '0')
    this.setAttribute('aria-valuemax', String(this.steps.length))
    this.setAttribute('aria-valuenow', String(selected))
    this.setAttribute('aria-disabled', String(this.disabled))
    this.setAttribute('aria-readonly', String(this.readonly))

    if (this.label) {
      this.setAttribute('aria-label', this.label)
    } else {
      this.removeAttribute('aria-label')
    }

    if (selectedStep) {
      this.setAttribute('aria-valuetext', selectedStep.title)
    } else {
      this.removeAttribute('aria-valuetext')
    }
  }

  private readonly renderSymbol = (
    step: StepperStep,
    value: number,
    displayValue: number
  ): StepperTemplate => {
    const hovered = this.hoverValue === value
    const selected = displayValue >= value
    const symbol = html`<span
      class="symbol"
      style="--facade-stepper-symbol-color: ${tokenColor(
        step.color,
        selected ? 10 : 4
      )}"
    >
      <wa-icon
        canvas="auto"
        library="phosphor"
        name="${value === 1 ? 'tag-simple-fill' : 'tag-chevron-fill'}"
      ></wa-icon>
    </span>`

    return hovered
      ? html`<span
          slot="anchor"
          class="step"
          data-step="${value}"
          data-hovered
          aria-hidden="true"
        >
          ${symbol}
        </span>`
      : html`<span
          slot="anchor"
          class="step"
          data-step="${value}"
          aria-hidden="true"
        >
          ${symbol}
        </span>`
  }

  private readonly renderPopup = (
    step: StepperStep,
    symbol: StepperTemplate,
    active: boolean
  ): StepperTemplate => {
    const style = `--facade-stepper-tooltip-background: ${tokenColor(
      step.color,
      10
    )}; --facade-stepper-tooltip-color: ${tooltipColor(step.color)}`
    const contents = html`${symbol}<span class="tooltip" role="tooltip"
        >${step.title}</span
      >`

    return active
      ? html`<wa-popup
          placement="top"
          distance="8"
          arrow
          arrow-placement="anchor"
          flip
          shift
          active
          style="${style}"
        >
          ${contents}
        </wa-popup>`
      : html`<wa-popup
          placement="top"
          distance="8"
          arrow
          arrow-placement="anchor"
          flip
          shift
          style="${style}"
        >
          ${contents}
        </wa-popup>`
  }

  private readonly renderStep = (
    step: StepperStep,
    index: number,
    displayValue: number
  ): StepperTemplate => {
    const value = index + 1
    const symbol = this.renderSymbol(step, value, displayValue)

    return this.noTooltips
      ? symbol
      : this.renderPopup(
          step,
          symbol,
          this.hoverValue === value && step.title.length > 0
        )
  }

  override render(): StepperTemplate {
    const selected = stepIndex(this.steps, this.value)
    const displayValue =
      this.disabled || this.readonly ? selected : this.hoverValue || selected
    const symbols = pipe(
      this.steps,
      map((step, index) => this.renderStep(step, index, displayValue))
    )

    return html`<span class="symbols" part="base">${symbols}</span>`
  }
}

FacadeStepperElement.define()

declare global {
  interface HTMLElementTagNameMap {
    'facade-stepper': FacadeStepperElement
  }
}
